import { Fragment, useContext, useMemo, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';

import * as topojson from 'topojson-client';
import { Circle } from '@visx/shape';
import { Mercator } from '@visx/geo';
import { Text } from '@visx/text';
import isFinite from 'lodash/isFinite';
import { localPoint } from '@visx/event';
import { scaleLinear } from '@visx/scale';
import { useTooltipInPortal } from '@visx/tooltip';
import { voronoi } from '@visx/voronoi';

import { formatGrowth, formatVolume } from 'utils/helpers';
import Stack from '../Stack';
import topology from '@/assets/topo.json';

const land = topojson.feature(topology, topology.objects.land);
const countries = topojson.feature(topology, {
    ...topology.objects.countries,
    geometries: topology.objects.countries.geometries.filter(({ properties }) =>
        ['FR', 'GB', 'JP', 'KR', 'US'].includes(properties.code),
    ),
});
const world = [...land.features, ...countries.features];

const SVG = styled.svg`
    border: 1px solid ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_md};
    overflow: hidden;
`;

const MapTooltipWrapper = styled(Stack).attrs(({ $space = '1rem' }) => ({
    $space,
}))`
    background-color: ${({ theme }) => theme.gray_7};
    color: ${({ theme }) => theme.gray_1};
    padding: ${({ theme }) => theme.space_8}px;
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontSemiBold};
    font-size: ${({ theme }) => theme.fontSize_base};
    line-height: ${({ theme }) => theme.space_9}px;
    box-shadow: ${({ theme }) => theme.box_shadow_1};
    width: 12.5rem;
`;

const TooltipName = styled.div`
    font-weight: ${({ theme }) => theme.fontSemiBold};
    color: ${({ theme }) => theme.gray_1};
`;

const TooltipMetric = styled.div`
    font-size: ${({ theme }) => theme.fontSize_lg};
    text-transform: uppercase;
`;

const TooltipLabel = styled.div`
    font-size: ${({ theme }) => theme.fontSize_xs};
    color: ${({ theme }) => theme.gray_3};
    text-transform: uppercase;
`;

function MapTooltip({ label, metric, name }) {
    return (
        <MapTooltipWrapper>
            <TooltipName>{name}</TooltipName>
            <TooltipMetric>{metric}</TooltipMetric>
            <TooltipLabel>{label}</TooltipLabel>
        </MapTooltipWrapper>
    );
}

function WorldMap({ width, height, data, sortOption, onMarketClick }) {
    const themeContext = useContext(ThemeContext);
    const [isVisible, setIsVisible] = useState(false);
    const [content, setContent] = useState(null);
    const [coords, setCoords] = useState({ x: -100000, y: -100000 });
    const svgRef = useRef();
    const { TooltipInPortal } = useTooltipInPortal({
        scroll: true,
        detectBounds: true,
    });

    if (width < 10) return null;

    const min = sortOption.sortBy === 'YoY' ? -1 : 0;

    const max = sortOption.sortBy === 'YoY' ? 1 : 0;

    const domain = [
        Math.min(
            min,
            ...data.map((f) => Math.abs(f[sortOption.sortBy])).filter(isFinite),
        ),
        Math.max(
            max,
            ...data.map((f) => Math.abs(f[sortOption.sortBy])).filter(isFinite),
        ),
    ];

    const maxRange = width / 10;

    const size = scaleLinear({
        domain,
        range: [0, maxRange],
    });

    let sortedData = data;

    // always render the content last so it's on top
    if (content && data) {
        sortedData = data.slice();
        sortedData.splice(
            sortedData.findIndex(({ code }) => code === content.code),
            1,
        );
        sortedData.push(content);
    }

    return (
        <>
            <SVG width={width} height={height} ref={svgRef}>
                <Mercator
                    data={world}
                    scale={(width / 570) * 100}
                    translate={[width * 0.44, height * 0.91]}
                >
                    {({ features }) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks -- useMemo in render prop; extracting to a component is a larger refactor
                        const voronoiLayout = useMemo(() => {
                            const vData = features.reduce(
                                (acc, { feature, centroid }) => {
                                    if (!feature.properties.code) return acc;
                                    acc.push({
                                        x: centroid[0],
                                        y: centroid[1],
                                        code: feature.properties.code,
                                    });
                                    return acc;
                                },
                                [],
                            );
                            return voronoi({
                                x: (d) => d.x,
                                y: (d) => d.y,
                                width,
                                height,
                            })(vData);
                        }, [width, height, features]);

                        const featuresByCode = features.reduce(
                            (acc, feature) => {
                                if (
                                    feature.feature &&
                                    feature.feature.properties &&
                                    feature.feature.properties.code
                                ) {
                                    acc[feature.feature.properties.code] =
                                        feature;
                                }
                                return acc;
                            },
                            {},
                        );

                        return (
                            <>
                                {features.map(({ feature, path }) => {
                                    return (
                                        <path
                                            key={`map-feature-path-${feature.id}`}
                                            d={path || ''}
                                            fill={themeContext.gray_100}
                                            stroke={themeContext.gray_5}
                                            strokeWidth={1}
                                        />
                                    );
                                })}
                                {sortedData.map((dataPoint) => {
                                    const feature =
                                        featuresByCode[dataPoint.code];
                                    if (!feature) return null;
                                    const { properties } = feature.feature;
                                    const { centroid } = feature;
                                    const { name, code: featureCode } =
                                        properties;
                                    const [centroidX, centroidY] = centroid;
                                    const country = data.find(
                                        ({ code }) => code === featureCode,
                                    );
                                    const v = (country || {})[
                                        sortOption.sortBy
                                    ];
                                    const r = v
                                        ? size(
                                              sortOption.sortBy === 'YoY'
                                                  ? Math.min(Math.abs(v), max)
                                                  : Math.abs(v),
                                          )
                                        : 0;
                                    const color = !(
                                        featureCode &&
                                        (v || v === 0)
                                    )
                                        ? themeContext.gray_100
                                        : v < 0
                                          ? themeContext.red_1
                                          : themeContext.purple_1;

                                    return featureCode ? (
                                        <Fragment key={dataPoint.id}>
                                            <Circle
                                                cx={centroidX}
                                                cy={centroidY}
                                                r={r}
                                                fill={color}
                                                fillOpacity={0.25}
                                                stroke={
                                                    isVisible &&
                                                    content.code === featureCode
                                                        ? themeContext.purple_4
                                                        : themeContext.gray_5
                                                }
                                                strokeWidth={1}
                                            />
                                            <Text
                                                fontSize=".875rem"
                                                fontFamily={
                                                    themeContext.style_primary
                                                }
                                                fontWeight={
                                                    themeContext.fontBold
                                                }
                                                textAnchor="middle"
                                                verticalAnchor="middle"
                                                width={r * 2}
                                                fill={
                                                    !(
                                                        isVisible &&
                                                        content.code ===
                                                            featureCode
                                                    )
                                                        ? themeContext.gray_2
                                                        : v < 0
                                                          ? themeContext.orange_700
                                                          : themeContext.purple_1
                                                }
                                                x={centroidX}
                                                y={centroidY}
                                            >
                                                {country ? country.name : name}
                                            </Text>
                                        </Fragment>
                                    ) : null;
                                })}
                                <rect
                                    width={width}
                                    height={height}
                                    fill="transparent"
                                    strokeWidth={0}
                                    css={`
                                        cursor: ${
                                            content ? 'pointer' : 'default'
                                        };
                                    `}
                                    onMouseMove={(e) => {
                                        if (!svgRef.current) return;

                                        const point = localPoint(
                                            svgRef.current,
                                            e,
                                        );
                                        if (!point) return;

                                        const closest = voronoiLayout.find(
                                            point.x,
                                            point.y,
                                            maxRange,
                                        );

                                        if (closest) {
                                            const country = data.find(
                                                ({ code }) =>
                                                    code === closest.data.code,
                                            );
                                            setContent(country);
                                            setIsVisible(true);
                                            setCoords({
                                                x: e.clientX,
                                                y: e.clientY,
                                            });
                                        } else {
                                            setIsVisible(false);
                                            setContent(null);
                                            setCoords({
                                                x: -Infinity,
                                                y: -Infinity,
                                            });
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        setIsVisible(false);
                                        setContent(null);
                                        setCoords({
                                            x: -Infinity,
                                            y: -Infinity,
                                        });
                                    }}
                                    onClick={() => {
                                        if (content) onMarketClick(content);
                                    }}
                                />
                            </>
                        );
                    }}
                </Mercator>
            </SVG>
            {isVisible && content ? (
                <div css="position: relative;">
                    <TooltipInPortal
                        style={{ position: 'absolute', zIndex: 13 }}
                        left={coords.x}
                        top={coords.y}
                    >
                        <MapTooltip
                            name={content.name}
                            metric={(sortOption.sortBy === 'YoY'
                                ? formatGrowth
                                : formatVolume)(content[sortOption.sortBy])}
                            label={sortOption.label}
                        />
                    </TooltipInPortal>
                </div>
            ) : null}
        </>
    );
}

export default WorldMap;
